import { useState, useEffect } from 'react'
import { getCustomerConfig } from 'modules/customer-config'
import type { CustomerConfigKey, CustomerConfigValue } from 'modules/customer-config'

/**
 * Fetches a typed customer configuration value and tracks loading state.
 * Returns null for data when the key is not configured for the customer.
 *
 * Usage:
 *   import { CustomerConfigKey } from 'modules/customer-config'
 *
 *   const { data, isLoading } = useCustomerConfig(
 *     clientPublicKey,
 *     CustomerConfigKey.EMPLOYEE_EXPERIENCE__ENABLE_SHOPPING_EXPERIENCE
 *   )
 *   // data is inferred as boolean | null
 */
export const useCustomerConfig = <K extends CustomerConfigKey>(
  clientPublicKey: string,
  key: K
): { data: CustomerConfigValue<K> | null; isLoading: boolean } => {
  const [data, setData] = useState<CustomerConfigValue<K> | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!clientPublicKey) {
      setData(null)
      setIsLoading(true)
      return
    }

    let cancelled = false

    setData(null)
    setIsLoading(true)

    getCustomerConfig(clientPublicKey, key)
      .then((result) => { if (!cancelled) setData(result) })
      .finally(() => { if (!cancelled) setIsLoading(false) })

    return () => { cancelled = true }
  }, [clientPublicKey, key])

  return { data, isLoading }
}
